"use client";

import React, { useState } from "react";
import { ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export type FAQItem = {
  id?: string | number;
  question: JSX.Element | string;
  answer: JSX.Element | string;
};

type Props = {
  data: FAQItem[];
  showAllLimit?: number;
  initialOpenIndex?: number | null;
  className?: string;
  containerClassName?: string;
  questionClassName?: string;
  answerClassName?: string;
  separatorClassName?: string;
};

const FAQAccordion: React.FC<Props> = ({
  data,
  showAllLimit,
  initialOpenIndex = null,
  className,
  containerClassName,
  questionClassName,
  answerClassName,
  separatorClassName,
}) => {
  const [openIndex, setOpenIndex] = useState<number | null>(initialOpenIndex);
  const [showAll, setShowAll] = useState(false);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  // Show limited FAQs initially if showAllLimit is set, all FAQs when showAll is true
  const displayedFAQs =
    showAllLimit && !showAll ? data.slice(0, showAllLimit) : data;

  return (
    <div
      className={cn(
        "flex w-full flex-col items-center justify-center gap-8",
        className
      )}
    >
      {/* FAQ Items Container */}
      <div
        className={cn(
          "w-full max-w-[660px] px-6 py-[30px]",
          containerClassName
        )}
      >
        <div className="flex flex-col items-center justify-center gap-8">
          {displayedFAQs.map((faq, index) => {
            const isOpen = openIndex === index;
            const key = faq.id ?? index;
            return (
              <div
                key={key}
                className="flex w-full flex-col items-start justify-center gap-6"
              >
                {/* Entire FAQ item - clickable */}
                <button
                  onClick={() => toggleFAQ(index)}
                  className="flex w-full flex-col items-start justify-start gap-6 transition-opacity hover:opacity-80"
                  aria-label={isOpen ? "Collapse" : "Expand"}
                >
                  {/* Question Row */}
                  <div className="inline-flex w-full items-center justify-between">
                    <div
                      className={cn(
                        "flex-1 text-balance pr-4 text-left font-figtree text-base font-medium leading-5 text-foreground",
                        questionClassName
                      )}
                    >
                      {typeof faq.question === "string"
                        ? faq.question
                        : faq.question}
                    </div>
                    <div className="flex items-center justify-center gap-2.5 rounded-md p-1.5 outline outline-1 outline-offset-[-1px] outline-foreground/20">
                      <ChevronDown
                        className={cn(
                          "h-5 w-5 shrink-0 text-foreground transition-transform duration-200",
                          isOpen && "rotate-180"
                        )}
                      />
                    </div>
                  </div>

                  {/* Answer */}
                  {isOpen && (
                    <div
                      className={cn(
                        "w-full text-left font-figtree text-base font-normal leading-6 text-muted-foreground",
                        answerClassName
                      )}
                      // `text-pretty` as a class gets silently dropped: this repo's
                      // tailwind-merge (1.14.0) predates text-wrap utility support and
                      // collides it with the text-color group, keeping only the last
                      // one. Setting it inline avoids the merge entirely. `textWrap`
                      // also predates this repo's csstype version, hence the cast.
                      style={{ textWrap: "pretty" } as React.CSSProperties}
                    >
                      {typeof faq.answer === "string" ? (
                        faq.answer
                      ) : (
                        <span>{faq.answer}</span>
                      )}
                    </div>
                  )}
                </button>

                {/* Separator */}
                <div
                  className={cn(
                    "h-0 w-full outline outline-1 outline-offset-[-0.50px] outline-[#1c2a3d]",
                    separatorClassName
                  )}
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* See all FAQs Button */}
      {showAllLimit && data.length > showAllLimit && (
        <Button
          variant="outline"
          onClick={() => setShowAll(!showAll)}
          className="h-11 rounded-lg border border-border bg-transparent px-6 py-2.5 font-figtree text-base font-normal leading-6 text-foreground hover:bg-foreground/10"
        >
          {showAll ? "Show less" : "See all FAQs"}
        </Button>
      )}
    </div>
  );
};

export default FAQAccordion;
